const EFFECT_TARGET_SELECTOR = [
	'.laybutton',
	'.lay-menu-button',
	'.lay-menu-item-button',
	'#lay-woocommerce .button',
	'.lay-content a.button.add_to_cart_button',
	'.lay-content a.button.product_type_variable',
	'.xoo-wsc-container .xoo-wsc-ft-buttons-cont .xoo-wsc-ft-btn',
].join(', ')

const customPropertyEnabled = (styles, property) => styles.getPropertyValue(property).trim() === '1'

const createContent = (nodes, textSlideEnabled = false) => {
	const content = document.createElement('span')
	content.className = 'lay-button-effect__content'
	if (!textSlideEnabled) {
		nodes.forEach((node) => content.appendChild(node))
		return content
	}

	content.classList.add('lay-button-effect__content--text-slide')
	const sizer = document.createElement('span')
	sizer.className = 'lay-button-effect__text-sizer'
	sizer.setAttribute('aria-hidden', 'true')
	const clip = document.createElement('span')
	clip.className = 'lay-button-effect__text-clip'
	const current = document.createElement('span')
	current.className = 'lay-button-effect__text lay-button-effect__text--current'
	const next = document.createElement('span')
	next.className = 'lay-button-effect__text lay-button-effect__text--next'
	next.setAttribute('aria-hidden', 'true')
	nodes.forEach((node) => {
		const sizeClone = node.cloneNode(true)
		const nextClone = node.cloneNode(true)
		if (sizeClone instanceof Element) sanitizeHiddenClone(sizeClone)
		if (nextClone instanceof Element) sanitizeHiddenClone(nextClone)
		sizer.appendChild(sizeClone)
		current.appendChild(node)
		next.appendChild(nextClone)
	})
	clip.appendChild(current)
	clip.appendChild(next)
	content.appendChild(sizer)
	content.appendChild(clip)
	return content
}

const createFill = () => {
	const fill = document.createElement('span')
	fill.className = 'lay-button-effect__fill'
	fill.setAttribute('aria-hidden', 'true')
	return fill
}

const sanitizeHiddenClone = (clone) => {
	if (!(clone instanceof Element)) return
	if (clone.removeAttribute) {
		clone.removeAttribute('id')
		clone.removeAttribute('name')
		clone.removeAttribute('for')
		clone.setAttribute('tabindex', '-1')
	}
	clone.querySelectorAll('[id], [name], [for], [tabindex]').forEach((element) => {
		element.removeAttribute('id')
		element.removeAttribute('name')
		element.removeAttribute('for')
		element.setAttribute('tabindex', '-1')
	})
}

const createFace = (className, content, fillEnabled, hidden = false) => {
	const face = document.createElement('span')
	face.className = `lay-button-effect__face ${className}`
	if (hidden) face.setAttribute('aria-hidden', 'true')
	if (fillEnabled) face.appendChild(createFill())
	face.appendChild(content)
	return face
}

const copyCurrentVisualState = (face, styles) => {
	const visualProperties = ['color', 'background', 'border', 'border-radius', 'box-shadow', '-webkit-backdrop-filter', 'backdrop-filter']
	visualProperties.forEach((property) => {
		const value = styles.getPropertyValue(property)
		if (value) face.style.setProperty(property, value, 'important')
	})
}

const update3dDepth = (button, box, flipDirection) => {
	const measure = () => {
		if (!button.isConnected) return
		const rect = box.getBoundingClientRect()
		const horizontal = flipDirection === 'left-to-right' || flipDirection === 'right-to-left'
		const size = horizontal ? rect.width : rect.height
		if (size > 0) button.style.setProperty('--lay-button-effect-depth', `${size / 2}px`)
	}
	window.requestAnimationFrame(measure)
	if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure)
}

const enhanceButton = (button) => {
	if (!(button instanceof HTMLElement) || button.dataset.layButtonEffectsReady === '1') return

	const styles = window.getComputedStyle(button)
	const effect3d = customPropertyEnabled(styles, '--lay-button-effect-3d')
	const effectFill = customPropertyEnabled(styles, '--lay-button-effect-fill')
	const effectTextSlide = customPropertyEnabled(styles, '--lay-button-effect-text-slide')
	const flipDirection = styles.getPropertyValue('--lay-button-effect-flip-direction').trim() || 'bottom-to-top'
	if (!effect3d && !effectFill && !effectTextSlide) return
	if (button instanceof HTMLInputElement) {
		// Void elements cannot contain the faces/fill layers. Mark them so CSS
		// keeps the conventional hover-color fallback instead.
		button.dataset.layButtonEffectsReady = '1'
		button.dataset.layButtonEffectsUnsupported = '1'
		if (effectFill) button.dataset.layButtonEffectFill = '1'
		if (effectTextSlide) button.dataset.layButtonEffectTextSlide = '1'
		return
	}

	const originalNodes = Array.from(button.childNodes)
	const content = createContent(originalNodes, effectTextSlide)
	button.dataset.layButtonEffectsReady = '1'
	if (effectFill) button.dataset.layButtonEffectFill = '1'
	if (effectTextSlide) button.dataset.layButtonEffectTextSlide = '1'

	if (!effect3d) {
		if (effectFill) button.appendChild(createFill())
		button.appendChild(content)
		return
	}

	button.setAttribute('data-lay-button-effect-3d', '1')
	const motion = document.createElement('span')
	motion.className = 'lay-button-effect__motion'
	const box = document.createElement('span')
	box.className = 'lay-button-effect__box'
	const hiddenContent = content.cloneNode(true)
	sanitizeHiddenClone(hiddenContent)
	const frontFace = createFace('lay-button-effect__face--front', content, effectFill)
	// Preserve the element's already-resolved visual state. This also keeps
	// active menu buttons correct when their active-state CSS overrides the
	// normal Lay Button colors before the 3D wrapper is installed.
	copyCurrentVisualState(frontFace, styles)
	box.appendChild(frontFace)
	box.appendChild(createFace('lay-button-effect__face--top', hiddenContent, effectFill, true))
	motion.appendChild(box)
	button.appendChild(motion)
	update3dDepth(button, box, flipDirection)
}

const enhanceButtons = (root = document) => {
	if (root instanceof Element && root.matches(EFFECT_TARGET_SELECTOR)) enhanceButton(root)
	root.querySelectorAll(EFFECT_TARGET_SELECTOR).forEach(enhanceButton)
}

const button_mouseover_effects_controller = {
	i: function () {
		enhanceButtons()
		window.laytheme.on('newpageshown', function () {
			enhanceButtons()
		})
	},
}

export default button_mouseover_effects_controller
